import React, { useState, useEffect } from 'react';
import { images } from '../../assets';
import logoSvg from '../../assets/images/logo-small.svg';
import './WaitlistPopup.css';

interface WaitlistPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const WaitlistPopup: React.FC<WaitlistPopupProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    confirmEmail: ''
  });

  const [error, setError] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      confirmEmail: ''
    });
    setError('');
    setIsValid(false);
    setIsSubmitting(false);
    setIsSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    const validateForm = () => {
      const isEmailValid = validateEmail(formData.email);
      const doEmailsMatch = formData.email.toLowerCase() === formData.confirmEmail.toLowerCase();
      const isNameFilled = formData.name.trim() !== '';
      
      setIsValid(isEmailValid && doEmailsMatch && isNameFilled);
    };

    validateForm();
  }, [formData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Clear error when user types
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate email format
    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate email match
    if (formData.email.toLowerCase() !== formData.confirmEmail.toLowerCase()) {
      setError('Email addresses do not match');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await fetch(`${API_URL}/waitlist/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 400 && errorData.detail === "Email already registered in waitlist") {
          setError('This email is already registered in our waitlist');
        } else {
          throw new Error('Failed to submit form');
        }
        return;
      }

      setIsSuccess(true);
    } catch (err) {
      setError('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="waitlist-popup-overlay">
      <div className="waitlist-popup">
        {!isSuccess ? (
          <>
            <button className="waitlist-popup-close" onClick={handleClose}>×</button>
            
            <h2 className="waitlist-popup-title">Join waitlist</h2>
            <p className="waitlist-popup-subtitle">Be the first to hear about the launch of Heal</p>

            <form onSubmit={handleSubmit} className="waitlist-popup-form">
              <div className="form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <input
                  type="email"
                  name="confirmEmail"
                  placeholder="Confirm email"
                  value={formData.confirmEmail}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {error && <p className="waitlist-popup-error">{error}</p>}

              <button 
                type="submit" 
                className={`waitlist-popup-submit ${!isValid || isSubmitting ? 'disabled' : ''}`}
                disabled={!isValid || isSubmitting}
              >
                <img src={images.ctaLight} alt="Submit" />
              </button>
            </form>
          </>
        ) : (
          <div className="waitlist-popup-success">
            <img src={logoSvg} alt="Heal" className="waitlist-popup-logo" />
            <h2 className="waitlist-popup-success-title">Thank you</h2>
            <p className="waitlist-popup-success-message">
              You'll be one of the first to experience Heal.
            </p>
            <button 
              className="waitlist-popup-back-button"
              onClick={handleClose}
            >
              <img src={images.backToWebCta} alt="Back to website" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WaitlistPopup; 